/**
 * useFormPayload - Transform officer notification form data to API request payloads
 *
 * Matches Python API's OfficerAppointRequest / OfficerChangeRequest / OfficerCeaseRequest
 */

// ===== Payload Types =====

export interface OfficerAppointPayload {
  session_id: string;
  company_no: string;
  officer_type: string;
  identification_type: string;
  identification_number: string;
  person_name: string;
  date_of_birth: string;
  nationality: string;
  gender: string;
  race: string;
  business_occupation: string;
  business_occupation_detail?: string;
  email: string;
  mobile_number?: string;
  // Residential Address
  residential_country: string;
  residential_state?: string;
  residential_city?: string;
  residential_postcode?: string;
  residential_street01: string;
  residential_street02?: string;
  residential_street03?: string;
  // Service Address (Director only)
  same_as_above: boolean;
  service_country?: string;
  service_state?: string;
  service_city?: string;
  service_postcode?: string;
  service_street01?: string;
  service_street02?: string;
  service_street03?: string;
  // Appointment details
  designation: string;
  alternate_to_director_name?: string;
  date_of_appointment: string;
  // Attachment
  attachment_filename: string;
  attachment_content: string;
  // EOT
  extension_of_time: boolean;
  eot_granted_date?: string;
  eot_attachment?: string;
  eot_attachment_name?: string;
  // Directorship
  directorships?: Array<{
    companyNo: string;
    companyName: string;
    dateAppointed?: string;
    dateCeased?: string;
    source: string;
  }>;
}

export interface OfficerChangePayload {
  session_id: string;
  company_no: string;
  officer_type: string;
  pre_identification_number: string;
  // Changed fields (all optional)
  identification_type?: string;
  identification_number?: string;
  person_name?: string;
  nationality?: string;
  business_occupation?: string;
  business_occupation_detail?: string;
  email?: string;
  mobile_number?: string;
  // Address change
  residential_country?: string;
  residential_state?: string;
  residential_city?: string;
  residential_postcode?: string;
  residential_street01?: string;
  residential_street02?: string;
  residential_street03?: string;
  // Date
  date_of_change: string;
  // EOT
  extension_of_time: boolean;
  eot_granted_date?: string;
  eot_attachment?: string;
  eot_attachment_name?: string;
}

export interface OfficerCeasePayload {
  session_id: string;
  company_no: string;
  officer_type: string;
  identification_number: string;
  designation: string;
  alternate_to_director_name?: string;
  date_of_cessation: string;
  reason_for_cessation: string;
  other_reason?: string;
  attachment_filename?: string;
  attachment_content?: string;
  // EOT
  extension_of_time: boolean;
  eot_granted_date?: string;
  eot_attachment?: string;
  eot_attachment_name?: string;
}

// ===== Helpers =====

const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

function toISODate(dateStr: string): string {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const match = dateStr.match(/^(\d{1,2})\s+([A-Z]+)\s+(\d{4})$/);
  if (match) {
    const monthIdx = MONTH_NAMES.indexOf(match[2]);
    if (monthIdx >= 0) {
      const day = match[1].padStart(2, '0');
      const month = String(monthIdx + 1).padStart(2, '0');
      return `${match[3]}-${month}-${day}`;
    }
  }
  return dateStr;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Extract EOT fields from formData.eot */
async function extractEot(formData: Record<string, any>): Promise<{
  extension_of_time: boolean;
  eot_granted_date?: string;
  eot_attachment?: string;
  eot_attachment_name?: string;
}> {
  const eot = formData.eot || {};
  const isEot = !!eot.enabled;
  let eotAttachment: string | undefined;
  let eotAttachmentName: string | undefined;
  if (isEot && eot.attachment) {
    const file = Array.isArray(eot.attachment) ? eot.attachment[0] : eot.attachment;
    if (file instanceof File) {
      eotAttachmentName = file.name;
      eotAttachment = await fileToBase64(file);
    }
  }
  return {
    extension_of_time: isEot,
    eot_granted_date: isEot ? toISODate(eot.grantedDate || '') : undefined,
    eot_attachment: eotAttachment,
    eot_attachment_name: eotAttachmentName,
  };
}

// ===== Payload Builders =====

export async function buildAppointPayload(
  formData: Record<string, any>,
  sessionId: string,
  officerType: string,
): Promise<OfficerAppointPayload> {
  // appointData is a flat object from DirectorAppointForm.buildEmitData()
  const d = formData.appointData || {};
  const sameAsAbove = d.sameAsAbove !== false;

  // Attachment (resolutionAttachment is File[] or null)
  let attachmentFilename = '';
  let attachmentContent = '';
  const resFile = Array.isArray(d.resolutionAttachment) ? d.resolutionAttachment[0] : d.resolutionAttachment;
  if (resFile instanceof File) {
    attachmentFilename = resFile.name;
    attachmentContent = await fileToBase64(resFile);
  }

  const eot = await extractEot(formData);

  console.log('[useFormPayload] buildAppointPayload directorships →', {
    hasOtherDirectorship: d.hasOtherDirectorship,
    otherDirectorships: d.otherDirectorships,
  });

  return {
    session_id: sessionId,
    company_no: formData.companyNo || '',
    officer_type: officerType,
    identification_type: d.idType || '',
    identification_number: d.idNumber || '',
    person_name: d.name || '',
    date_of_birth: toISODate(d.dob || ''),
    nationality: d.nationality || '',
    gender: d.gender || '',
    race: d.race || '',
    business_occupation: d.businessOccupation || '',
    business_occupation_detail: d.businessOccupationDetail || undefined,
    email: d.email || '',
    mobile_number: d.mobile || undefined,
    // Residential
    residential_country: d.residentialCountry || 'MALAYSIA',
    residential_state: d.residentialState || undefined,
    residential_city: d.residentialCity || undefined,
    residential_postcode: d.residentialPostcode || undefined,
    residential_street01: d.residentialStreet01 || '',
    residential_street02: d.residentialStreet02 || undefined,
    residential_street03: d.residentialStreet03 || undefined,
    // Service (Director only)
    same_as_above: sameAsAbove,
    service_country: !sameAsAbove ? (d.serviceCountry || undefined) : undefined,
    service_state: !sameAsAbove ? (d.serviceState || undefined) : undefined,
    service_city: !sameAsAbove ? (d.serviceCity || undefined) : undefined,
    service_postcode: !sameAsAbove ? (d.servicePostcode || undefined) : undefined,
    service_street01: !sameAsAbove ? (d.serviceStreet01 || undefined) : undefined,
    service_street02: !sameAsAbove ? (d.serviceStreet02 || undefined) : undefined,
    service_street03: !sameAsAbove ? (d.serviceStreet03 || undefined) : undefined,
    // Appointment
    designation: d.appointDesignation || 'DIRECTOR',
    alternate_to_director_name: d.alternateDirectorName || undefined,
    date_of_appointment: toISODate(d.dateOfAppointment || ''),
    // Attachment
    attachment_filename: attachmentFilename,
    attachment_content: attachmentContent,
    // EOT
    ...eot,
    // Directorship
    directorships: d.hasOtherDirectorship === 'YES' && Array.isArray(d.otherDirectorships) && d.otherDirectorships.length > 0
      ? d.otherDirectorships.map((ds: any) => ({
          companyNo: ds.companyNo || '',
          companyName: ds.companyName || '',
          dateAppointed: ds.dateAppointed || undefined,
          dateCeased: ds.dateCeased || undefined,
          source: ds.source || 'registered',
        }))
      : undefined,
  };
}

export async function buildChangePayload(
  formData: Record<string, any>,
  sessionId: string,
  officerType: string,
): Promise<OfficerChangePayload> {
  const changeInfo = formData.changeInfo || {};
  const address = formData._addresses?.residential || {};
  const hasAddress = !!(address.street01 || address.country);
  const eot = await extractEot(formData);

  return {
    session_id: sessionId,
    company_no: formData.companyNo || '',
    officer_type: officerType,
    pre_identification_number: formData.preIdNumber || '',
    // Changed fields (only non-empty)
    identification_type: changeInfo.idType || undefined,
    identification_number: changeInfo.idNumber || undefined,
    person_name: changeInfo.name || undefined,
    nationality: changeInfo.nationality || undefined,
    business_occupation: changeInfo.businessOccupation || undefined,
    business_occupation_detail: changeInfo.businessOccupationDetail || undefined,
    email: changeInfo.email || undefined,
    mobile_number: changeInfo.mobile || undefined,
    // Address (only if filled)
    residential_country: hasAddress ? (address.country || undefined) : undefined,
    residential_state: hasAddress ? (address.state || undefined) : undefined,
    residential_city: hasAddress ? (address.city || undefined) : undefined,
    residential_postcode: hasAddress ? (address.postcode || undefined) : undefined,
    residential_street01: hasAddress ? (address.street01 || undefined) : undefined,
    residential_street02: hasAddress ? (address.street02 || undefined) : undefined,
    residential_street03: hasAddress ? (address.street03 || undefined) : undefined,
    // Date
    date_of_change: toISODate(formData.dateOfChange || ''),
    // EOT
    ...eot,
  };
}

export async function buildCeasePayload(
  formData: Record<string, any>,
  sessionId: string,
  officerType: string,
): Promise<OfficerCeasePayload> {
  const cessation = formData.cessationDetails || {};

  // Attachment (Director only)
  let attachmentFilename: string | undefined;
  let attachmentContent: string | undefined;
  if (cessation.attachment instanceof File) {
    attachmentFilename = cessation.attachment.name;
    attachmentContent = await fileToBase64(cessation.attachment);
  }

  const eot = await extractEot(formData);

  return {
    session_id: sessionId,
    company_no: formData.companyNo || '',
    officer_type: officerType,
    identification_number: formData.idNumber || '',
    designation: cessation.designation || 'DIRECTOR',
    alternate_to_director_name: cessation.alternateDirectorName || undefined,
    date_of_cessation: toISODate(cessation.dateOfCessation || ''),
    reason_for_cessation: cessation.reasonForCessation || '',
    other_reason: cessation.otherReason || undefined,
    attachment_filename: attachmentFilename,
    attachment_content: attachmentContent,
    // EOT
    ...eot,
  };
}
